/**
 * Override Re-Review Queue — Phase 1c.2
 * Surfaces P12.5 override observations needing manual outcome classification.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import {
  getOverrideRereviewQueue,
  recordOverrideOutcome,
  type OverrideOutcomeKind,
} from "@/data/contractor/networkExecution";
import { getPlanningObservationLog } from "@/data/planningReview/observationLog";
import { useRole } from "@/data/RoleContext";
import { toast } from "sonner";

const OUTCOMES: OverrideOutcomeKind[] = ["positive", "neutral", "negative"];

export function OverrideRereviewQueue() {
  const { overrideOutcomes, setOverrideOutcomes } = useAppData();
  const { role } = useRole();

  const overrideObservations = useMemo(() => {
    return getPlanningObservationLog()
      .filter((o) => o.eventType === "override_applied")
      .map((o) => ({ observationId: o.id, jobId: o.jobId }));
  }, []);

  const queue = useMemo(
    () => getOverrideRereviewQueue(overrideObservations, overrideOutcomes),
    [overrideObservations, overrideOutcomes],
  );

  const [target, setTarget] = useState<{ observationId: string; jobId: string } | null>(null);
  const [outcome, setOutcome] = useState<OverrideOutcomeKind>("neutral");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!target) return;
    const r = recordOverrideOutcome({
      records: overrideOutcomes,
      observationId: target.observationId,
      jobId: target.jobId,
      outcome,
      reviewerId: role,
      reviewerRole: role,
      reason,
    });
    if (!r.record) {
      setError(r.error ?? "Could not record outcome.");
      return;
    }
    setOverrideOutcomes(r.records);
    toast.success("Override outcome recorded.");
    setTarget(null);
    setReason("");
    setOutcome("neutral");
  };

  if (queue.length === 0) {
    return (
      <Card className="p-3 text-sm text-muted-foreground" data-testid="rereview-empty">
        No overrides awaiting re-review.
      </Card>
    );
  }

  return (
    <>
      <Card className="p-0 overflow-hidden" data-testid="rereview-queue">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase">
            <tr>
              <th className="text-left p-2">Observation</th>
              <th className="text-left p-2">Job</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Reason</th>
              <th className="text-right p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row) => (
              <tr key={row.observationId} className="border-t">
                <td className="p-2 font-mono text-xs">{row.observationId}</td>
                <td className="p-2 text-xs">{row.jobId}</td>
                <td className="p-2">
                  <Badge
                    variant={row.currentOutcome === "negative" ? "destructive" : "outline"}
                    className="text-[10px] capitalize"
                  >
                    {row.currentOutcome}
                  </Badge>
                </td>
                <td className="p-2 text-xs text-muted-foreground">
                  {row.reason === "never_reviewed" ? "Never reviewed" : "Negative outcome"}
                </td>
                <td className="p-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setTarget({
                        observationId: row.observationId,
                        jobId: row.jobId,
                      })
                    }
                  >
                    Record outcome
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record override outcome</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Outcome</label>
              <div className="mt-1 flex gap-2">
                {OUTCOMES.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOutcome(o)}
                    className={`px-3 py-1 rounded-md text-xs border capitalize ${
                      outcome === o
                        ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                        : "bg-card hover:bg-muted"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Reason (≥ 10 characters)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="What was learned from this override?"
              />
            </div>
            {error && <div className="text-xs text-destructive">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submit}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
